<template>
  <transition name="overlay-fade">
    <div v-if="modelValue" class="modal-overlay" @click.self="close">
      <div class="modal" role="dialog" aria-modal="true" aria-label="Édition du portfolio">
        <div class="modal-header">
          <div class="title-stack">
            <input
                v-model="localItem.title"
                type="text"
                class="title-input"
                placeholder="Titre"
            >
            <input
                v-model="localItem.subTitle"
                type="text"
                class="subtitle-input"
                placeholder="Sous-titre"
            >
          </div>

          <button
              type="button"
              class="close-btn"
              aria-label="Fermer"
              title="Fermer la fenêtre d’édition"
              @click="close"
          >
            &times;
          </button>
        </div>

        <div class="modal-body" :class="{ 'no-media': !hasMedia }">
          <div class="gallery">
            <div v-if="hasMedia" class="main-media-wrapper">
              <button
                  v-if="mediaList.length > 1"
                  type="button"
                  class="nav left"
                  aria-label="Média précédent"
                  title="Afficher le média précédent"
                  @click="prev"
              >
                &lsaquo;
              </button>

              <template v-if="currentMedia && currentMedia.type === 'image'">
                <img :src="currentMedia.src" :alt="localItem.title || 'media'" class="main-media">
              </template>
              <template v-else-if="currentMedia && currentMedia.type === 'video'">
                <video class="main-media" :src="currentMedia.src" controls preload="metadata" playsinline></video>
              </template>
              <template v-else-if="currentMedia && currentMedia.type === 'pdf'">
                <iframe class="main-media" :src="currentMedia.src" title="Aperçu PDF"></iframe>
              </template>
              <template v-else>
                <div class="main-file">Fichier</div>
              </template>

              <button
                  v-if="mediaList.length > 1"
                  type="button"
                  class="nav right"
                  aria-label="Média suivant"
                  title="Afficher le média suivant"
                  @click="next"
              >
                &rsaquo;
              </button>
            </div>

            <div v-else class="empty-media">
              <span>Aucun média</span>
            </div>

            <transition-group name="thumb-flow" tag="div" class="thumbnails">
              <div
                  v-for="(media, index) in mediaList"
                  :key="`thumb-${index}-${media.path}`"
                  class="thumb-card"
                  :class="{ active: index === currentIndex, dragging: mediaDragIndex === index }"
                  draggable="true"
                  @dragstart="onMediaDragStart(index, $event)"
                  @dragenter.prevent="onMediaDragOver(index, $event)"
                  @dragover.prevent="onMediaDragOver(index, $event)"
                  @drop.prevent="onMediaDrop"
                  @dragend="onMediaDragEnd"
              >
                <button
                    type="button"
                    class="thumb-btn"
                    :title="'Afficher le média ' + (index + 1)"
                    @click="goTo(index)"
                    @dblclick="editMediaPath(index)"
                >
                  <template v-if="media.type === 'image'">
                    <img :src="media.src" :alt="'miniature ' + (index + 1)">
                  </template>
                  <template v-else-if="media.type === 'video'">
                    <video :src="media.src" muted preload="metadata"></video>
                  </template>
                  <template v-else-if="media.type === 'pdf'">
                    <div class="thumb-file">PDF</div>
                  </template>
                  <template v-else>
                    <div class="thumb-file">FILE</div>
                  </template>
                </button>

                <button
                    type="button"
                    class="thumb-delete"
                    aria-label="Supprimer le média"
                    title="Supprimer ce média"
                    @click.stop="removeMedia(index)"
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M11 1.5v1h3a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 10.615 16h-5.23a2 2 0 0 1-1.994-1.84L2.538 3.5H2a.5.5 0 0 1 0-1h3v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5"/>
                  </svg>
                </button>
              </div>

              <button
                  type="button"
                  class="thumb-card thumb-card--add"
                  :disabled="uploadingMedia"
                  :title="uploadingMedia ? 'Envoi du média en cours' : 'Ajouter un média à la fin'"
                  @click="openMediaPicker"
              >
                {{ uploadingMedia ? '...' : '+' }}
              </button>
            </transition-group>
            <input
                ref="mediaInput"
                type="file"
                class="media-input"
                accept="image/*,video/*,.pdf"
                @change="onMediaFileChange"
            >
          </div>

          <div class="content-area">
            <RichTextEditor v-model="localItem.description"/>

            <div class="tech-section">
              <transition-group name="chip-flow" tag="div" class="tech-list">
                <div
                    v-for="(technology, index) in selectedTechnologies"
                    :key="`tech-${technology.id}`"
                    class="tech-chip"
                    :class="{ dragging: techDragIndex === index }"
                    draggable="true"
                    @dragstart="onTechnologyDragStart(index, $event)"
                    @dragenter.prevent="onTechnologyDragOver(index, $event)"
                    @dragover.prevent="onTechnologyDragOver(index, $event)"
                    @drop.prevent="onTechnologyDrop"
                    @dragend="onTechnologyDragEnd"
                >
                  <span>{{ technology.name }}</span>

                  <button
                      type="button"
                      class="tech-delete"
                      aria-label="Retirer la technologie"
                      title="Retirer cette technologie de l'item"
                      @click="removeTechnology(index)"
                  >
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M11 1.5v1h3a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 10.615 16h-5.23a2 2 0 0 1-1.994-1.84L2.538 3.5H2a.5.5 0 0 1 0-1h3v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5"/>
                    </svg>
                  </button>
                </div>

                <button
                    type="button"
                    class="tech-chip tech-chip--add"
                    title="Ajouter ou sélectionner des technologies"
                    @click="techPickerVisible = true"
                >
                  +
                </button>
              </transition-group>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <div class="modal-status">
            <p v-if="hasUnsavedChanges" class="dirty-text">Modifications non sauvegardées</p>
            <p v-if="errorText" class="error-text">{{ errorText }}</p>
          </div>

          <button
              type="button"
              class="accent-btn"
              :disabled="saving"
              title="Enregistrer les modifications de cet item"
              @click="save"
          >
            {{ saving ? '...' : 'Enregistrer' }}
          </button>
        </div>

        <TechnologyPickerModal
            v-model="techPickerVisible"
            :selected-ids="localItem.technologyIds || []"
            :technologies="allTechnologies"
            :create-technology="createTechnology"
            :delete-technology="deleteTechnology"
            :creating="creatingTechnology"
            @apply="applyTechnologySelection"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import RichTextEditor from "@/components/RichTextEditor.vue";
import TechnologyPickerModal from "@/components/TechnologyPickerModal.vue";
import {PostRequest} from "@/services/request";
import {inferMediaType, toPortfolioMediaList} from "@/utils/portfolioMedia";

function moveArrayEntry(list, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) {
    return list;
  }

  const next = [...list];
  const [entry] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, entry);
  return next;
}

function createLocalItem(source = {}) {
  return {
    description: '',
    displayOrder: 0,
    id: null,
    media: [],
    subTitle: '',
    technologyIds: [],
    title: '',
    ...JSON.parse(JSON.stringify(source || {}))
  };
}

function serializeItemState(source = {}) {
  const item = createLocalItem(source);
  return JSON.stringify({
    description: item.description || '',
    media: (item.media || []).map((media) => ({
      path: String(media.path || ''),
      type: String(media.type || inferMediaType(media.path || ''))
    })),
    subTitle: item.subTitle || '',
    technologyIds: [...(item.technologyIds || [])],
    title: item.title || ''
  });
}

function formatBytes(value) {
  const size = Number(value || 0);
  if (size <= 0) {
    return '0 o';
  }

  const units = ['o', 'Ko', 'Mo', 'Go'];
  let unitIndex = 0;
  let current = size;
  while (current >= 1024 && unitIndex < units.length - 1) {
    current /= 1024;
    unitIndex += 1;
  }

  const digits = current >= 100 ? 0 : current >= 10 ? 1 : 2;
  return `${current.toFixed(digits)} ${units[unitIndex]}`;
}

export default {
  name: 'AdminPortfolioModal',

  components: {
    RichTextEditor,
    TechnologyPickerModal
  },

  props: {
    allTechnologies: {
      type: Array,
      default: () => []
    },
    createTechnology: {
      type: Function,
      default: null
    },
    creatingTechnology: {
      type: Boolean,
      default: false
    },
    deleteTechnology: {
      type: Function,
      default: null
    },
    errorText: {
      type: String,
      default: ''
    },
    clearStatus: {
      type: Function,
      default: null
    },
    confirmAction: {
      type: Function,
      default: null
    },
    item: {
      type: Object,
      required: true
    },
    modelValue: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    setStatus: {
      type: Function,
      default: null
    }
  },

  emits: ['save', 'update:modelValue'],

  data() {
    return {
      currentIndex: 0,
      initialSnapshot: serializeItemState(),
      localItem: createLocalItem(),
      uploadedMediaPaths: [],
      mediaDragIndex: -1,
      mediaOrigin: [],
      mediaPreviewDirty: false,
      mediaSwapLock: '',
      uploadingMedia: false,
      techDragIndex: -1,
      techOrigin: [],
      techPreviewDirty: false,
      techSwapLock: '',
      techPickerVisible: false
    };
  },

  computed: {
    mediaList() {
      return toPortfolioMediaList(this.localItem.media || []);
    },

    hasMedia() {
      return this.mediaList.length > 0;
    },

    currentMedia() {
      return this.mediaList[this.currentIndex] || null;
    },

    hasUnsavedChanges() {
      return serializeItemState(this.localItem) !== this.initialSnapshot;
    },

    selectedTechnologies() {
      const lookup = new Map((this.allTechnologies || []).map((technology) => [technology.id, technology]));

      return (this.localItem.technologyIds || [])
          .map((technologyId) => lookup.get(technologyId))
          .filter(Boolean);
    }
  },

  watch: {
    item: {
      deep: true,
      handler(nextItem) {
        this.localItem = createLocalItem(nextItem);
        this.initialSnapshot = serializeItemState(nextItem);
        this.uploadedMediaPaths = [];
      }
    },

    modelValue(value) {
      if (value) {
        this.localItem = createLocalItem(this.item);
        this.initialSnapshot = serializeItemState(this.item);
        this.uploadedMediaPaths = [];
        this.currentIndex = 0;
        document.body.classList.add('no-scroll');
        return;
      }

      this.techPickerVisible = false;
      document.body.classList.remove('no-scroll');
    },

    'localItem.media': {
      deep: true,
      handler() {
        const list = Array.isArray(this.localItem.media) ? this.localItem.media : [];
        if (!list.length) {
          this.currentIndex = 0;
          return;
        }

        if (this.currentIndex >= list.length) {
          this.currentIndex = list.length - 1;
        }
      }
    }
  },

  beforeUnmount() {
    document.body.classList.remove('no-scroll');
  },

  methods: {
    async close() {
      if (this.hasUnsavedChanges && this.confirmAction) {
        const confirmed = await this.confirmAction({
          confirmLabel: 'Fermer',
          danger: true,
          message: 'Des modifications non sauvegardees seront perdues.',
          title: 'Fermer sans enregistrer'
        });
        if (!confirmed) {
          return;
        }
      }

      await this.cleanupUploadedMedia();

      this.$emit('update:modelValue', false);
    },

    save() {
      this.syncMediaOrder();
      this.$emit('save', createLocalItem(this.localItem));
    },

    prev() {
      if (!this.hasMedia) {
        return;
      }

      this.currentIndex = (this.currentIndex - 1 + this.mediaList.length) % this.mediaList.length;
    },

    next() {
      if (!this.hasMedia) {
        return;
      }

      this.currentIndex = (this.currentIndex + 1) % this.mediaList.length;
    },

    goTo(index) {
      this.currentIndex = index;
    },

    openMediaPicker() {
      if (this.uploadingMedia) {
        return;
      }

      if (this.$refs.mediaInput) {
        this.$refs.mediaInput.value = '';
        this.$refs.mediaInput.click();
      }
    },

    async onMediaFileChange(event) {
      const file = event?.target?.files?.[0];
      if (!file) {
        return;
      }

      this.uploadingMedia = true;
      if (this.setStatus) {
        this.setStatus({
          message: file.name,
          detail: `0 o / ${formatBytes(file.size)}`,
          mode: 'progress',
          progress: 0,
          title: 'Envoi du média',
          variant: 'info'
        });
      }

      try {
        const uploadStartedAt = Date.now();
        const contentBase64 = await this.fileToBase64(file);
        const uploadedMedia = await PostRequest('admin/media-upload', {
          contentBase64,
          fileName: file.name,
          mimeType: file.type
        }, {
          onUploadProgress: (progressEvent) => {
            if (!this.setStatus) {
              return;
            }

            const total = Number(progressEvent.total || 0);
            const loaded = Number(progressEvent.loaded || 0);
            const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
            const elapsedSeconds = Math.max((Date.now() - uploadStartedAt) / 1000, 0.1);
            const speed = loaded / elapsedSeconds;
            this.setStatus({
              message: file.name,
              detail: `${formatBytes(loaded)} / ${formatBytes(total || file.size)} • ${formatBytes(speed)}/s`,
              mode: 'progress',
              progress,
              title: 'Envoi du média',
              variant: 'info'
            });
          }
        });

        if (!Array.isArray(this.localItem.media)) {
          this.localItem.media = [];
        }

        this.localItem.media.push({
          displayOrder: this.localItem.media.length,
          path: uploadedMedia.path,
          type: inferMediaType(uploadedMedia.path)
        });
        this.uploadedMediaPaths.push(uploadedMedia.path);
        this.currentIndex = this.localItem.media.length - 1;
        this.syncMediaOrder();
        if (this.setStatus) {
          this.setStatus({
            detail: `${formatBytes(file.size)} / ${formatBytes(file.size)} • terminé`,
            message: 'Le média a été ajouté à l’item',
            mode: 'progress',
            progress: 100,
            title: 'Envoi terminé',
            variant: 'success'
          });
        }
        this.clearStatus?.(1500);
      } catch (error) {
        this.setStatus?.({
          message: error?.response?.data?.error || error?.response?.data?.detail || 'Envoi du média impossible',
          mode: 'spinner',
          title: 'Erreur',
          variant: 'error'
        });
        this.clearStatus?.(2200);
      } finally {
        this.uploadingMedia = false;
        if (event?.target) {
          event.target.value = '';
        }
      }
    },

    editMediaPath(index) {
      const currentPath = this.localItem.media?.[index]?.path || '/media/';
      const nextPath = window.prompt('Modifier le chemin du média', currentPath);
      const normalizedPath = String(nextPath || '').trim();
      if (!normalizedPath || !this.localItem.media?.[index]) {
        return;
      }

      this.localItem.media[index] = {
        ...this.localItem.media[index],
        path: normalizedPath,
        type: inferMediaType(normalizedPath)
      };
    },

    async removeMedia(index) {
      if (this.confirmAction) {
        const confirmed = await this.confirmAction({
          confirmLabel: 'Supprimer',
          danger: true,
          message: 'Ce média sera retiré de l’item en cours.',
          title: 'Supprimer le média'
        });
        if (!confirmed) {
          return;
        }
      }

      const mediaPath = this.localItem.media?.[index]?.path;
      if (this.uploadedMediaPaths.includes(mediaPath)) {
        await this.deleteTemporaryMedia(mediaPath);
        this.uploadedMediaPaths = this.uploadedMediaPaths.filter((path) => path !== mediaPath);
      }

      this.localItem.media.splice(index, 1);
      this.syncMediaOrder();

      if (this.currentIndex >= this.localItem.media.length) {
        this.currentIndex = Math.max(this.localItem.media.length - 1, 0);
      }
    },

    syncMediaOrder() {
      this.localItem.media = (this.localItem.media || []).map((media, index) => ({
        ...media,
        displayOrder: index
      }));
    },

    onMediaDragStart(index, event) {
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.dropEffect = 'move';
        event.dataTransfer.setDragImage(event.currentTarget, 41, 41);
      }

      this.mediaDragIndex = index;
      this.mediaOrigin = [...(this.localItem.media || [])];
      this.mediaPreviewDirty = false;
    },

    onMediaDragOver(index, event) {
      if (this.mediaDragIndex < 0 || index === this.mediaDragIndex) {
        return;
      }

      const rect = event?.currentTarget?.getBoundingClientRect?.();
      if (rect) {
        const progress = (event.clientX - rect.left) / Math.max(rect.width, 1);
        const shouldSwap = this.mediaDragIndex < index ? progress >= 0.66 : progress <= 0.34;
        const swapKey = `${this.mediaDragIndex}:${index}:${this.mediaDragIndex < index ? 'after' : 'before'}`;

        if (this.mediaSwapLock === swapKey && !shouldSwap) {
          this.mediaSwapLock = '';
        }

        if (!shouldSwap || this.mediaSwapLock === swapKey) {
          return;
        }

        this.mediaSwapLock = swapKey;
      }

      this.localItem.media = moveArrayEntry(this.localItem.media || [], this.mediaDragIndex, index);
      this.mediaDragIndex = index;
      this.currentIndex = index;
      this.mediaPreviewDirty = true;
      this.syncMediaOrder();
    },

    onMediaDrop() {
      this.syncMediaOrder();
    },

    onMediaDragEnd() {
      if (!this.mediaPreviewDirty && this.mediaOrigin.length) {
        this.localItem.media = [...this.mediaOrigin];
      }

      this.mediaDragIndex = -1;
      this.mediaOrigin = [];
      this.mediaPreviewDirty = false;
      this.mediaSwapLock = '';
      this.syncMediaOrder();
    },

    removeTechnology(index) {
      this.localItem.technologyIds.splice(index, 1);
    },

    onTechnologyDragStart(index, event) {
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.dropEffect = 'move';
        event.dataTransfer.setDragImage(event.currentTarget, event.currentTarget.offsetWidth / 2, event.currentTarget.offsetHeight / 2);
      }

      this.techDragIndex = index;
      this.techOrigin = [...(this.localItem.technologyIds || [])];
      this.techPreviewDirty = false;
    },

    onTechnologyDragOver(index, event) {
      if (this.techDragIndex < 0 || index === this.techDragIndex) {
        return;
      }

      const rect = event?.currentTarget?.getBoundingClientRect?.();
      if (rect) {
        const progress = (event.clientX - rect.left) / Math.max(rect.width, 1);
        const shouldSwap = this.techDragIndex < index ? progress >= 0.66 : progress <= 0.34;
        const swapKey = `${this.techDragIndex}:${index}:${this.techDragIndex < index ? 'after' : 'before'}`;

        if (this.techSwapLock === swapKey && !shouldSwap) {
          this.techSwapLock = '';
        }

        if (!shouldSwap || this.techSwapLock === swapKey) {
          return;
        }

        this.techSwapLock = swapKey;
      }

      this.localItem.technologyIds = moveArrayEntry(this.localItem.technologyIds || [], this.techDragIndex, index);
      this.techDragIndex = index;
      this.techPreviewDirty = true;
    },

    onTechnologyDrop() {
      return;
    },

    onTechnologyDragEnd() {
      if (!this.techPreviewDirty && this.techOrigin.length) {
        this.localItem.technologyIds = [...this.techOrigin];
      }

      this.techDragIndex = -1;
      this.techOrigin = [];
      this.techPreviewDirty = false;
      this.techSwapLock = '';
    },

    async deleteTemporaryMedia(mediaPath) {
      if (!mediaPath) {
        return;
      }

      try {
        await PostRequest('admin/media-delete', {path: mediaPath});
      } catch (_error) {
        return;
      }
    },

    async cleanupUploadedMedia() {
      const remainingPaths = [...new Set(this.uploadedMediaPaths)];
      this.uploadedMediaPaths = [];

      for (const mediaPath of remainingPaths) {
        await this.deleteTemporaryMedia(mediaPath);
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const [, base64 = ''] = result.split(',');
          if (!base64) {
            reject(new Error('Conversion du fichier impossible.'));
            return;
          }

          resolve(base64);
        };
        reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
        reader.readAsDataURL(file);
      });
    },

    applyTechnologySelection(nextIds) {
      this.localItem.technologyIds = [...nextIds];
    }
  }
}
</script>

<style scoped>
:global(body.no-scroll) {
  overflow: hidden;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 28px 16px;
  box-sizing: border-box;
}

.modal {
  background-color: #323336;
  color: #ffffff;
  width: min(1240px, calc(100vw - 32px));
  min-width: 300px;
  border-radius: 20px;
  box-shadow: 0 0 20px #00f0ff, 0 0 30px #00f0ff66;
  padding: 1rem 1.5rem;
  max-height: calc(100dvh - 56px);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.title-stack {
  flex: 1;
  display: grid;
  gap: 0.5rem;
}

.title-input,
.subtitle-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #50545d;
  border-radius: 14px;
  background: #252629;
  color: white;
  padding: 0.85rem 1rem;
}

.title-input {
  font-size: 1.4rem;
  color: #00f0ff;
  font-weight: 700;
}

.close-btn {
  border: none;
  background: transparent;
  color: #fff;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.modal-body.no-media {
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1fr);
}

.gallery {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.main-media-wrapper,
.empty-media {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}

.main-media,
.main-file,
.empty-media {
  width: 100%;
  height: 500px;
  display: block;
  border: none;
  background: #000;
}

.main-media {
  object-fit: contain;
}

.main-file,
.empty-media {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d7deea;
}

.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav.left {
  left: 8px;
}

.nav.right {
  right: 8px;
}

.thumbnails {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 8px;
  flex-wrap: nowrap;
  box-sizing: border-box;
}

.thumb-card {
  position: relative;
  width: 82px;
  height: 82px;
  flex: 0 0 auto;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid transparent;
  background: #202124;
  opacity: 0.88;
  transition: transform 0.2s cubic-bezier(0.2, 0.9, 0.2, 1), opacity 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.thumb-card.active {
  opacity: 1;
  border-color: #00f0ff;
  box-shadow: 0 0 0 1px rgba(0, 240, 255, 0.2), 0 8px 20px rgba(0, 0, 0, 0.25);
}

.thumb-card.dragging,
.tech-chip.dragging {
  opacity: 0.32;
  transform: scale(0.95);
}

.thumb-btn {
  width: 100%;
  height: 100%;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.thumb-btn img,
.thumb-btn video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb-file {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #45474d;
  color: #d7deea;
  font-size: 0.8rem;
}

.thumb-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgb(255, 0, 0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.18s ease, filter 0.18s ease;
}

.thumb-delete svg,
.tech-delete svg {
  width: 22px;
  height: 22px;
  fill: currentColor;
}

.tech-delete svg {
  width: 20px;
  height: 20px;
}

.thumb-delete:hover {
  transform: scale(1.22);
  filter: drop-shadow(0 0 8px rgba(255, 0, 0, 0.45));
}

.thumb-card--add {
  border: 2px dashed #00f0ff66;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #00f0ff;
  font-size: 2rem;
  cursor: pointer;
  background: rgba(0, 240, 255, 0.08);
}

.media-input {
  display: none;
}

.thumb-card--add:hover {
  transform: translateY(-2px);
  border-color: #00f0ff;
}

.thumb-flow-move,
.chip-flow-move {
  transition: transform 0.2s cubic-bezier(0.2, 0.9, 0.2, 1);
}

.content-area {
  background: #2a2b2e;
  border-radius: 12px;
  padding: 1rem;
  min-width: 0;
  display: grid;
  gap: 1rem;
}

.tech-section {
  display: grid;
  gap: 0.75rem;
}

.tech-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.6rem;
}

.tech-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.75rem;
  padding-right: 1.35rem;
  border: 2px solid #00f0ff;
  color: #e6faff;
  border-radius: 999px;
  background: rgba(0, 240, 255, 0.06);
  box-shadow: 0 0 8px #00f0ff33 inset;
  font-size: 0.9rem;
  line-height: 1;
  white-space: nowrap;
  cursor: grab;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.tech-chip--add {
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  font-size: 1.1rem;
  cursor: pointer;
}

.tech-delete {
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 2;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: rgb(255, 0, 0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.tech-delete:hover {
  transform: scale(1.2);
  filter: drop-shadow(0 0 8px rgba(255, 0, 0, 0.4));
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-top: 0.75rem;
}

.modal-status {
  display: grid;
  gap: 0.25rem;
}

.dirty-text {
  margin: 0;
  color: #ffcf66;
  font-size: 0.88rem;
}

.accent-btn {
  margin-left: auto;
  border-radius: 999px;
  border: 1px solid #00f0ff;
  cursor: pointer;
  padding: 0.7rem 1rem;
  background: rgba(0, 240, 255, 0.12);
  color: #00f0ff;
}

.error-text {
  color: #ff8a8a;
  margin: 0;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.22s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

@media (max-width: 980px) {
  .modal-body,
  .modal-body.no-media {
    grid-template-columns: 1fr;
  }

  .main-media,
  .main-file,
  .empty-media {
    height: 260px;
  }
}

@media (max-width: 720px) {
  .modal {
    width: calc(100vw - 20px);
    padding: 0.85rem;
  }

  .modal-header,
  .modal-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .close-btn {
    align-self: flex-end;
  }

  .accent-btn {
    margin-left: 0;
  }
}
</style>
